import { FC } from 'react';
import FlexBlock from '@/components/ui/FlexBlock/FlexBlock.tsx';
import breakpoints from '@/constants/breakpoints.ts';
import styles from './TripEmissionLog.module.scss';

interface ActionNeededSelectionBarProps {
	selectedTripsCount: number;
	selectedTripsOnPageCount: number;
	allTripsSelected: boolean;
	allTripsOnPageSelected: boolean;
	selectionScopeDescription: string;
	hasSelectedComplianceGapType: boolean;
	isCheckingRemainingTrips: boolean;
	hasRemainingTripsError: boolean;
	remainingTripsCount?: number;
	isSelectingRemainingTrips: boolean;
	isResolvingTrips: boolean;
	onRetryRemainingTrips: () => void;
	onSelectRemainingTrips: () => void;
	onDeselectAll: () => void;
	onResolve: () => void;
}

const ActionNeededSelectionBar: FC<ActionNeededSelectionBarProps> = ({
	selectedTripsCount,
	selectedTripsOnPageCount,
	allTripsSelected,
	allTripsOnPageSelected,
	selectionScopeDescription,
	hasSelectedComplianceGapType,
	isCheckingRemainingTrips,
	hasRemainingTripsError,
	remainingTripsCount,
	isSelectingRemainingTrips,
	isResolvingTrips,
	onRetryRemainingTrips,
	onSelectRemainingTrips,
	onDeselectAll,
	onResolve,
}) => {
	if (!selectedTripsCount) {
		return null;
	}

	const selectionDisabled = isSelectingRemainingTrips || isResolvingTrips;
	const selectedTripsLabel = `${selectedTripsCount === 1 ? 'trip' : 'trips'}${selectionScopeDescription}`;
	const selectedTripsOnPageLabel = `${selectedTripsOnPageCount === 1 ? 'trip' : 'trips'}${selectionScopeDescription}`;

	return (
		<FlexBlock
			className={styles.selectionBar}
			justifyContent="space-between"
			alignItems={{ [breakpoints.zero]: 'stretch', [breakpoints.tabletSmall]: 'center' }}
			flexDirection={{ [breakpoints.zero]: 'column', [breakpoints.tabletSmall]: 'row' }}
			rowGap="12px"
			aria-live="polite"
		>
			<div className={styles.selectionMessage}>
				<span>
					{allTripsSelected
						? `All ${selectedTripsCount} ${selectedTripsLabel} ${selectedTripsCount === 1 ? 'is' : 'are'} selected.`
						: allTripsOnPageSelected
							? `All ${selectedTripsOnPageCount} ${selectedTripsOnPageLabel} ${selectedTripsOnPageCount === 1 ? 'is' : 'are'} selected on this page.`
							: `${selectedTripsCount} ${selectedTripsLabel} ${selectedTripsCount === 1 ? 'is' : 'are'} selected.`}
				</span>
				{hasSelectedComplianceGapType && allTripsOnPageSelected && isCheckingRemainingTrips && (
					<span className={styles.selectionScopeStatus}>Checking remaining trips…</span>
				)}
				{hasSelectedComplianceGapType && allTripsOnPageSelected && hasRemainingTripsError && (
					<>
						<span className={styles.selectionScopeStatus}>We couldn’t check the remaining trips.</span>
						<button type="button" onClick={onRetryRemainingTrips} className={styles.selectRemainingButton}>
							Try again
						</button>
					</>
				)}
				{allTripsOnPageSelected && remainingTripsCount !== undefined && remainingTripsCount > 0 && (
					<button
						type="button"
						onClick={onSelectRemainingTrips}
						disabled={selectionDisabled}
						className={styles.selectRemainingButton}
					>
						{isSelectingRemainingTrips
							? 'Selecting remaining trips…'
							: `Select remaining ${remainingTripsCount} ${remainingTripsCount === 1 ? 'trip' : 'trips'}${selectionScopeDescription}`}
					</button>
				)}
			</div>
			<FlexBlock className={styles.selectionActions} alignItems="center" columnGap="12px">
				<button
					type="button"
					className={styles.clearSelectionButton}
					onClick={onDeselectAll}
					disabled={selectionDisabled}
				>
					Deselect all
				</button>
				<button
					type="button"
					className={styles.resolveSelectedButton}
					onClick={onResolve}
					disabled={selectionDisabled}
				>
					Resolve
				</button>
			</FlexBlock>
		</FlexBlock>
	);
};

export default ActionNeededSelectionBar;
